// ═══════════════════════════════════════════════════════════════════════════
// PrivacyPolicy — politique de confidentialité, FR/EN selon la route
// (/privacy ou /en/privacy). Contenu aligné sur ce que l'app fait VRAIMENT :
// email optionnel, position en direct opt-in à durée limitée, médias sur GCS,
// emails Mailjet, crashs Sentry, pas de pub ni de revente.
// Mise à jour du contenu = éditer CONTENT ci-dessous + changer `updated`.
// ═══════════════════════════════════════════════════════════════════════════

import LegalPage from "./LegalPage";

const CONTACT = <a href="mailto:contact@getdispo.com">contact@getdispo.com</a>;

const CONTENT = {
  fr: {
    eyebrow: "Dispo",
    title: "Politique de confidentialité",
    updated: "Dernière mise à jour : 24 août 2026",
    sections: [
      {
        h: "Qui sommes-nous",
        p: [
          <>
            Dispo est une application qui aide les groupes d'amis à se voir
            plus souvent : tu partages tes disponibilités, tes potes répondent,
            et vous organisez vos sorties. Cette politique explique quelles
            données l'application traite, pourquoi, et quels sont tes droits.
            Pour toute question : {CONTACT}.
          </>,
        ],
      },
      {
        h: "Les données que nous collectons",
        list: [
          <>
            <strong>Ton compte</strong> : un pseudo, un mot de passe (chiffré)
            ou une connexion Apple/Google, une photo de profil (optionnelle),
            la langue de l'app. L'adresse email est <strong>optionnelle</strong>
            {" "}: elle sert à récupérer ton compte si tu oublies ton mot de
            passe (ou nous est transmise par Apple/Google si tu les utilises
            pour te connecter).
          </>,
          <>
            <strong>Ce que tu partages dans l'app</strong> : tes dispos et
            sondages, tes crews, tes messages (texte, photos, vidéos, messages
            vocaux, GIF, réactions).
          </>,
          <>
            <strong>Ta position</strong> : uniquement si tu actives
            volontairement le partage de position en direct dans une
            conversation, pour la durée que tu choisis. Le partage s'arrête
            automatiquement à la fin. En dehors de ça, l'app ne collecte
            jamais ta position.
          </>,
          <>
            <strong>Données techniques</strong> : modèle d'appareil et système,
            rapports de plantage anonymisés, jeton de notifications push.
          </>,
        ],
      },
      {
        h: "Ce que nous en faisons",
        p: [
          <>
            Fournir le service, tout simplement : afficher tes dispos à tes
            amis, acheminer les messages en temps réel, envoyer les
            notifications, sécuriser la plateforme et répondre à tes demandes
            de support.
          </>,
          <>
            <strong>Nous ne vendons jamais tes données</strong>, il n'y a pas
            de publicité dans l'app, et nous ne faisons aucun profilage
            publicitaire.
          </>,
        ],
      },
      {
        h: "Qui voit quoi",
        p: [
          <>
            Tes contenus sont visibles des personnes que tu as choisies : tes
            amis pour tes dispos, les participants d'une conversation pour les
            messages, les membres d'un crew pour le crew. Si tu partages un
            lien d'invitation, la personne qui l'ouvre voit la dispo concernée
            et peut y répondre depuis son navigateur.
          </>,
        ],
      },
      {
        h: "Nos prestataires",
        p: [
          <>
            Pour faire tourner Dispo, nous utilisons des prestataires qui
            traitent certaines données pour notre compte :
          </>,
        ],
        list: [
          <>
            <strong>Hébergement</strong> : OVHcloud (serveurs en Union
            européenne).
          </>,
          <>
            <strong>Photos, vidéos et messages vocaux</strong> : Google Cloud
            Storage.
          </>,
          <>
            <strong>Emails</strong> (code de récupération de mot de passe) :
            Mailjet (UE).
          </>,
          <>
            <strong>Rapports de plantage</strong> : Sentry (hébergement UE).
          </>,
          <>
            <strong>Notifications push</strong> : Expo, Apple (APNs) et Google
            (FCM).
          </>,
        ],
        after: [
          <>
            Nous pouvons aussi divulguer des données si la loi l'exige.
          </>,
        ],
      },
      {
        h: "Combien de temps",
        p: [
          <>
            Tes données sont conservées tant que ton compte est actif. Les
            partages de position expirent automatiquement à la fin de la durée
            choisie. Quand tu supprimes ton compte, tes données personnelles
            sont supprimées (hors copies de sauvegarde techniques, purgées à
            leur tour, et obligations légales).
          </>,
        ],
      },
      {
        h: "Tes droits",
        p: [
          <>
            Conformément au RGPD, tu peux accéder à tes données, les rectifier,
            les faire effacer, t'opposer à leur traitement ou en demander la
            portabilité.
          </>,
          <>
            Le plus simple : tu peux <strong>supprimer ton compte
            directement dans l'app</strong> (Paramètres → Compte), ou en faire
            la demande sur{" "}
            <a href="/askdelete">getdispo.com/askdelete</a>. Pour le reste,
            écris-nous à {CONTACT}.
          </>,
        ],
      },
      {
        h: "Âge minimum",
        p: [
          <>
            Dispo est réservé aux personnes de 13 ans et plus. Si tu as moins
            de 18 ans, assure-toi d'avoir l'accord d'un parent ou tuteur.
          </>,
        ],
      },
      {
        h: "Modifications",
        p: [
          <>
            Si cette politique évolue de façon importante, nous te préviendrons
            dans l'application. La date de dernière mise à jour figure en haut
            de cette page.
          </>,
        ],
      },
      {
        h: "Contact",
        p: [
          <>
            Pour toute question sur tes données ou cette politique :{" "}
            {CONTACT}.
          </>,
        ],
      },
    ],
  },
  en: {
    eyebrow: "Dispo",
    title: "Privacy Policy",
    updated: "Last updated: August 24, 2026",
    sections: [
      {
        h: "Who we are",
        p: [
          <>
            Dispo is an app that helps groups of friends see each other more
            often: you share when you're free, your friends reply, and you
            plan things together. This policy explains what data the app
            processes, why, and what your rights are. Questions? {CONTACT}.
          </>,
        ],
      },
      {
        h: "Data we collect",
        list: [
          <>
            <strong>Your account</strong>: a username, a password (encrypted)
            or an Apple/Google sign-in, an optional profile photo, and your
            app language. Your email address is <strong>optional</strong>: it
            is used to recover your account if you forget your password (or is
            provided by Apple/Google if you sign in with them).
          </>,
          <>
            <strong>What you share in the app</strong>: your dispos and polls,
            your crews, your messages (text, photos, videos, voice messages,
            GIFs, reactions).
          </>,
          <>
            <strong>Your location</strong>: only if you deliberately turn on
            live location sharing in a conversation, for the duration you
            choose. Sharing stops automatically when it ends. Outside of that,
            the app never collects your location.
          </>,
          <>
            <strong>Technical data</strong>: device model and OS, anonymised
            crash reports, push notification token.
          </>,
        ],
      },
      {
        h: "What we do with it",
        p: [
          <>
            We simply run the service: show your dispos to your friends,
            deliver messages in real time, send notifications, keep the
            platform safe and answer your support requests.
          </>,
          <>
            <strong>We never sell your data</strong>, there are no ads in the
            app, and we do no advertising profiling.
          </>,
        ],
      },
      {
        h: "Who sees what",
        p: [
          <>
            Your content is visible to the people you chose: your friends for
            your dispos, a conversation's participants for its messages, a
            crew's members for the crew. If you share an invite link, whoever
            opens it can see that dispo and reply from their browser.
          </>,
        ],
      },
      {
        h: "Our service providers",
        p: [
          <>
            To run Dispo, we rely on providers that process some data on our
            behalf:
          </>,
        ],
        list: [
          <>
            <strong>Hosting</strong>: OVHcloud (servers in the European
            Union).
          </>,
          <>
            <strong>Photos, videos and voice messages</strong>: Google Cloud
            Storage.
          </>,
          <>
            <strong>Emails</strong> (password recovery code): Mailjet (EU).
          </>,
          <>
            <strong>Crash reports</strong>: Sentry (EU hosting).
          </>,
          <>
            <strong>Push notifications</strong>: Expo, Apple (APNs) and
            Google (FCM).
          </>,
        ],
        after: [<>We may also disclose data where required by law.</>],
      },
      {
        h: "How long we keep it",
        p: [
          <>
            Your data is kept while your account is active. Live location
            shares expire automatically at the end of the chosen duration.
            When you delete your account, your personal data is deleted
            (except technical backups, purged in turn, and legal
            obligations).
          </>,
        ],
      },
      {
        h: "Your rights",
        p: [
          <>
            Under the GDPR you can access, rectify or erase your data, object
            to its processing, or request portability.
          </>,
          <>
            Easiest path: you can <strong>delete your account directly in the
            app</strong> (Settings → Account), or request it at{" "}
            <a href="/askdelete">getdispo.com/askdelete</a>. For anything
            else, write to {CONTACT}.
          </>,
        ],
      },
      {
        h: "Minimum age",
        p: [
          <>
            Dispo is for people aged 13 and over. If you are under 18, make
            sure you have a parent's or guardian's permission.
          </>,
        ],
      },
      {
        h: "Changes",
        p: [
          <>
            If this policy changes in a meaningful way, we will let you know
            in the app. The last-updated date is shown at the top of this
            page.
          </>,
        ],
      },
      {
        h: "Contact",
        p: [<>Any question about your data or this policy: {CONTACT}.</>],
      },
    ],
  },
};

const PrivacyPolicy = ({ lang = "fr" }) => (
  <LegalPage content={CONTENT[lang] || CONTENT.fr} lang={lang} />
);

export default PrivacyPolicy;
